/**
 * Премиальный glass-контейнер для emoji-иконки.
 * При hover родителя с `group` — primary glow усиливается, контейнер слегка
 * скейлится и поворачивается, emoji подсвечивается через drop-shadow.
 *
 * accent — необязательный hex-цвет для индивидуального оттенка (например, '#34a853').
 */
export function IconBadge({
  emoji,
  size = 'md',
  accent,
}: {
  emoji: string
  size?: 'sm' | 'md' | 'lg'
  accent?: string
}) {
  const dim = size === 'sm' ? 'w-11 h-11' : size === 'lg' ? 'w-16 h-16' : 'w-14 h-14'
  const textSize = size === 'sm' ? 'text-xl' : size === 'lg' ? 'text-3xl' : 'text-[28px]'

  // Inline-стиль для accent-tinted версии, иначе — defaults через tailwind utility
  const accentStyle = accent
    ? ({
        background: `linear-gradient(135deg, ${accent}1a 0%, ${accent}33 100%)`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 8px -2px ${accent}33`,
        borderColor: `${accent}33`,
      } as React.CSSProperties)
    : undefined

  return (
    <div
      style={accentStyle}
      className={`
        ${dim} rounded-2xl border flex items-center justify-center flex-shrink-0
        ${accent ? '' : 'bg-gradient-to-br from-purple-50 via-fuchsia-50 to-violet-100 border-purple-100/80'}
        shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_2px_8px_-2px_rgba(106,27,154,0.12)]
        transition-all duration-300 ease-out
        group-hover:scale-110 group-hover:-rotate-3
        group-hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_12px_28px_-6px_rgba(106,27,154,0.45)]
        group-hover:from-primary-soft group-hover:via-purple-100 group-hover:to-fuchsia-100
        group-hover:border-primary/40
        relative overflow-hidden
      `}
    >
      {/* Glossy highlight */}
      <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />
      {/* Soft glow at hover */}
      <div className="absolute -inset-1 rounded-2xl bg-primary/25 opacity-0 blur-xl group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      <span
        className={`relative ${textSize} drop-shadow-sm transition-transform duration-300 group-hover:scale-110`}
        style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.08))' }}
      >
        {emoji}
      </span>
    </div>
  )
}
